import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUploadOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import RefreshIcon from '@mui/icons-material/RefreshRounded';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useTranslation } from '../i18n';
import { PageHeader, Section } from '../components/Section';
import { RecentGenerationsStrip } from '../components/RecentGenerationsStrip';
import { getFingerprintNow } from '../hooks/useFingerprint';
import { useSignupNudge } from '../components/SignupNudge';
import { usePriceQuote } from '../hooks/usePriceQuote';

type UploadResult = { url: string; r2_key: string };

const ALL_AZIMUTHS = [
  'front view',
  'front-right quarter view',
  'right side view',
  'back-right quarter view',
  'back view',
  'back-left quarter view',
  'left side view',
  'front-left quarter view',
];
const ALL_ELEVATIONS = ['low-angle shot', 'eye-level shot', 'elevated shot', 'high-angle shot'];
const ALL_DISTANCES = ['close-up', 'medium shot', 'wide shot'];

const checkboxIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkboxCheckedIcon = <CheckBoxIcon fontSize="small" />;

async function uploadImage(file: File): Promise<UploadResult> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch('/api/v1/multi-angle/upload', {
    method: 'POST',
    credentials: 'include',
    body: fd,
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`upload failed: ${res.status} ${body}`);
  }
  return (await res.json()) as UploadResult;
}

function MultiSelect({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: string[];
  value: string[];
  onChange: (val: string[]) => void;
}) {
  return (
    <Autocomplete
      multiple
      size="small"
      options={options}
      value={value}
      onChange={(_, v) => onChange(v)}
      disableCloseOnSelect
      renderOption={(props, option, { selected }) => (
        <li {...props}>
          <Checkbox
            icon={checkboxIcon}
            checkedIcon={checkboxCheckedIcon}
            style={{ marginRight: 8 }}
            checked={selected}
            size="small"
          />
          {option}
        </li>
      )}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip label={option} {...getTagProps({ index })} size="small" key={option} />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          placeholder={value.length === 0 ? `All (${options.length})` : undefined}
        />
      )}
    />
  );
}

function ImageInput({
  value,
  onChange,
  onClear,
  label,
}: {
  value: UploadResult | null;
  onChange: (v: UploadResult) => void;
  onClear: () => void;
  label: string;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handle = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    setError(null);
    try {
      onChange(await uploadImage(f));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {label}
        <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>
      </Typography>
      {value ? (
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5, bgcolor: 'action.hover' }}
        >
          <Box
            component="img"
            src={value.url}
            alt={label}
            sx={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}
          />
          <Typography variant="body2" sx={{ flex: 1, fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12 }} noWrap>
            {value.r2_key.split('/').slice(-1)[0]}
          </Typography>
          <IconButton size="small" onClick={onClear}><DeleteIcon fontSize="small" /></IconButton>
        </Stack>
      ) : (
        <Button
          component="label"
          variant="outlined"
          fullWidth
          startIcon={busy ? <CircularProgress size={16} /> : <CloudUploadIcon />}
          sx={{ justifyContent: 'flex-start', py: 2.25, borderStyle: 'dashed', color: 'text.secondary' }}
          disabled={busy}
        >
          {busy ? 'Uploading…' : 'Choose image'}
          <input hidden type="file" accept="image/jpeg,image/png,image/webp" onChange={handle} />
        </Button>
      )}
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
    </Box>
  );
}

// Polls the activity until completion, then signs every result image and renders
// a grid. Multi-angle returns N images, so we use the multi-key presign instead
// of the single-result JobResult component.
function MultiAngleResult({
  activityId,
  onReset,
  resetLabel,
}: {
  activityId: number;
  onReset: () => void;
  resetLabel: string;
}) {
  const [status, setStatus] = useState<'pending' | 'processing' | 'completed' | 'failed'>('pending');
  const [urls, setUrls] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = async () => {
      try {
        const res = await fetch(`/api/v1/history/${activityId}`, { credentials: 'include' });
        if (!res.ok) throw new Error(`status fetch failed: ${res.status}`);
        const row = await res.json();
        if (cancelled) return;
        setStatus(row.job_status);
        const hasKeys = row.metadata?.r2_key || row.metadata?.r2_keys?.length;
        if (row.job_status === 'completed' && hasKeys) {
          const pres = await fetch(`/api/v1/history/${activityId}/presigned-url`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: '{}',
          });
          if (!pres.ok) throw new Error(`presign failed: ${pres.status}`);
          const data = await pres.json();
          if (!cancelled) setUrls(Array.isArray(data.urls) ? data.urls : []);
          return;
        }
        if (row.job_status === 'failed') {
          if (!cancelled) setError(row.error_message ?? 'Generation failed');
          return;
        }
        timer = setTimeout(tick, 3_000);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
        timer = setTimeout(tick, 5_000);
      }
    };
    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [activityId]);

  if (error) {
    return (
      <Stack spacing={2}>
        <Alert severity="error">{error}</Alert>
        <Box>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={onReset}>
            {resetLabel}
          </Button>
        </Box>
      </Stack>
    );
  }

  if (status !== 'completed' || urls.length === 0) {
    return (
      <Stack spacing={2}>
        <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
          {status === 'processing' ? 'Generating your views…' : 'Queued…'}
        </Typography>
        <LinearProgress sx={{ height: 8, borderRadius: 1 }} />
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          This can take a few minutes for many angles.
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {urls.length} image{urls.length !== 1 ? 's' : ''} generated
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: 1.5,
        }}
      >
        {urls.map((url, i) => (
          <Box
            key={i}
            sx={{
              position: 'relative',
              aspectRatio: '1 / 1',
              borderRadius: 1.5,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              '&:hover .dl': { opacity: 1 },
            }}
          >
            <Box component="img" src={url} alt={`view ${i + 1}`} loading="lazy" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            <IconButton
              className="dl"
              component="a"
              href={url}
              download
              target="_blank"
              rel="noopener"
              size="small"
              sx={{
                position: 'absolute',
                bottom: 4,
                right: 4,
                bgcolor: 'background.paper',
                opacity: 0,
                transition: 'opacity 0.2s',
              }}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
      </Box>
      <Stack direction="row" spacing={1.5}>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={onReset}>
          {resetLabel}
        </Button>
      </Stack>
    </Stack>
  );
}

export default function MultiAngle() {
  const t = useTranslation();
  const location = useLocation();
  const remix = (location.state as { remix?: Record<string, unknown> } | null)?.remix ?? null;

  const [image, setImage] = useState<UploadResult | null>(null);
  const [azimuth, setAzimuth] = useState<string[]>(
    (remix?.azimuth as string[] | undefined) ?? ['front view', 'right side view', 'back view', 'left side view']
  );
  const [elevation, setElevation] = useState<string[]>((remix?.elevation as string[] | undefined) ?? ['eye-level shot']);
  const [distance, setDistance] = useState<string[]>((remix?.distance as string[] | undefined) ?? ['medium shot']);
  const [submitting, setSubmitting] = useState(false);
  const [activityId, setActivityId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const effAzimuth = azimuth.length > 0 ? azimuth : ALL_AZIMUTHS;
  const effElevation = elevation.length > 0 ? elevation : ALL_ELEVATIONS;
  const effDistance = distance.length > 0 ? distance : ALL_DISTANCES;
  const expectedCount = effAzimuth.length * effElevation.length * effDistance.length;

  const canSubmit = !!image && !submitting;
  const perImage = usePriceQuote({ endpoint: 'multi-angle' });
  const totalCredits = perImage != null ? perImage * expectedCount : null;

  const { gateGenerate } = useSignupNudge();

  const handleSubmit = async () => {
    // Anonymous users get the signup nudge instead of a doomed 402.
    if (gateGenerate()) return;
    if (!image) return;
    setSubmitting(true);
    setError(null);
    try {
      const fingerprint = await getFingerprintNow();
      const res = await fetch('/api/v1/multi-angle/generate-async', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image_url: image.url,
          image_r2_key: image.r2_key,
          azimuth,
          elevation,
          distance,
          fingerprint,
        }),
      });
      if (res.status === 402) {
        const body = await res.json();
        throw new Error(`Insufficient credits (need ${body.required}, have ${body.available})`);
      }
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`submit failed: ${res.status} ${body}`);
      }
      const data = await res.json();
      setActivityId(data.activity_id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setActivityId(null);
    setImage(null);
  };

  return (
    <Box>
      <PageHeader title={t.multiAngle.title} description={t.multiAngle.description} />
      <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>

      {activityId ? (
        <Section title={t.multiAngle.resultTitle}>
          <Paper sx={{ p: 3 }}>
            <MultiAngleResult activityId={activityId} onReset={reset} resetLabel={t.multiAngle.newGeneration} />
          </Paper>
        </Section>
      ) : (
        <>
          <Section title={t.multiAngle.inputImage}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ maxWidth: 520 }}>
                <ImageInput label={t.multiAngle.inputImage} value={image} onChange={setImage} onClear={() => setImage(null)} />
              </Box>
            </Paper>
          </Section>

          <Section title={t.multiAngle.angleSelection} description={t.multiAngle.angleHint}>
            <Paper sx={{ p: 3 }}>
              <Stack spacing={2.5}>
                <MultiSelect label={t.multiAngle.azimuth} options={ALL_AZIMUTHS} value={azimuth} onChange={setAzimuth} />
                <MultiSelect label={t.multiAngle.elevation} options={ALL_ELEVATIONS} value={elevation} onChange={setElevation} />
                <MultiSelect label={t.multiAngle.distance} options={ALL_DISTANCES} value={distance} onChange={setDistance} />
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {t.multiAngle.willGenerate.replace('{count}', String(expectedCount))}
                </Typography>
              </Stack>
            </Paper>
          </Section>

          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

          <Stack direction="row" spacing={1.5}>
            <Button variant="contained" size="large" disabled={!canSubmit} onClick={handleSubmit}>
              {submitting ? (
                <CircularProgress size={20} sx={{ color: '#fff' }} />
              ) : (
                `${t.multiAngle.generate}${totalCredits != null ? ` (${totalCredits} ${t.credits.balanceUnit})` : ''}`
              )}
            </Button>
          </Stack>
        </>
      )}
      </Box>
      <RecentGenerationsStrip endpoint="multi-angle" />
      </Box>
    </Box>
  );
}
